import { INTERNAL_EMAIL_ON_JOB_ROLE_VALUES } from '@/constants/on-job-roles';

/**
 * A To/Cc/Bcc row on an internal email mixes three kinds of value in one
 * multi-select: individual users, job roles ("Hiring Managers (on job)") and
 * Kula roles (org roles). Roles stay as tokens all the way to the backend and
 * only resolve to people when the email is sent, against the job the candidate
 * is actually in — so the same activity reaches the right person on every job.
 *
 * Job roles already have unambiguous string values (`job_hiring_manager`), but
 * an org role is identified by a numeric id that would collide with a user id,
 * so it is prefixed. Mirrors the `pool:<id>` convention used by the automation
 * assignee picker.
 */
export const ORG_ROLE_TOKEN_PREFIX = 'org_role:';

export const orgRoleToken = (id: string | number) =>
  `${ORG_ROLE_TOKEN_PREFIX}${id}`;

export const isOrgRoleToken = (value: string) =>
  value.startsWith(ORG_ROLE_TOKEN_PREFIX);

export const orgRoleIdFromToken = (value: string) =>
  Number(value.slice(ORG_ROLE_TOKEN_PREFIX.length));

export const isJobRoleToken = (value: string) =>
  INTERNAL_EMAIL_ON_JOB_ROLE_VALUES.has(value);

export interface SplitRecipients {
  userIds: string[];
  participantGroups: string[];
  orgRoleIds: number[];
}

/**
 * Splits what the picker holds into the three arrays the API takes.
 * Anything that is not a role token is treated as a user id.
 */
export const splitRecipients = (
  values: (string | number | undefined | null)[] = []
): SplitRecipients => {
  const result: SplitRecipients = {
    userIds: [],
    participantGroups: [],
    orgRoleIds: [],
  };

  values.forEach((raw) => {
    if (raw === undefined || raw === null || raw === '') return;
    const value = String(raw);

    if (isJobRoleToken(value)) {
      result.participantGroups.push(value);
      return;
    }
    if (isOrgRoleToken(value)) {
      const id = orgRoleIdFromToken(value);
      if (!Number.isNaN(id)) result.orgRoleIds.push(id);
      return;
    }
    result.userIds.push(value);
  });

  return result;
};

export interface RecipientSeed {
  users?: { id?: string | number }[];
  participant_groups?: string[];
  org_roles?: { id?: string | number }[];
}

/**
 * Rebuilds the picker's value from a saved activity — the inverse of
 * {@link splitRecipients}.
 */
export const mergeRecipients = (seed: RecipientSeed | undefined): string[] => [
  ...(seed?.users ?? []).map((user) => String(user?.id ?? '')).filter(Boolean),
  ...(seed?.participant_groups ?? []),
  ...(seed?.org_roles ?? [])
    .filter((role) => role?.id !== undefined && role?.id !== null)
    .map((role) => orgRoleToken(role.id as string | number)),
];
